// # Revenue Calculations Component
import React from 'react';
import './bnct_revenue_calculations.css';

const RevenueCalculations = ({ revenueData, member }) => {
  // # Check if data is available
  if (!revenueData) return <div className="bnct_loading">Loading calculation data...</div>;

  // # Determine if this is a project budget account
  const isProjectBudget = member && member.id === 'project-budget';

  // # Debug: Log the originalAmounts to see what's available
  console.log("Original Amounts:", revenueData.originalAmounts);
  console.log("Revenue By Product:", revenueData.revenueByProduct);

  // # Create a list of products from revenueByProduct that are not in originalAmounts
  const productsInRevenueByProduct = Object.keys(revenueData.revenueByProduct || {});
  const productsInOriginalAmounts = Object.keys(revenueData.originalAmounts || {});
  
  // # Find products that are in revenueByProduct but not in originalAmounts
  const missingProducts = productsInRevenueByProduct.filter(
    product => !productsInOriginalAmounts.some(p => p.includes(product))
  );
  
  console.log("Missing Products:", missingProducts);

  // # Create entries for missing products
  const additionalEntries = {};
  missingProducts.forEach(product => {
    const netAmount = revenueData.revenueByProduct[product] || 0;
    const sharePercentage = getSharePercentage(member.id, product);
    
    // # Calculate gross revenue (before budget)
    // # For team members: netAmount is their share after budget
    // # We need to calculate what the total was before budget
    const budgetPercentage = 5; // Default budget percentage
    
    // # Calculate what the member's share would be if there was no budget
    const totalShareAmount = netAmount / (1 - budgetPercentage/100);
    
    // # Calculate gross revenue based on member's share percentage
    const grossRevenue = totalShareAmount / (sharePercentage/100);
    
    // # Calculate budget allocation
    const budgetAllocation = grossRevenue * (budgetPercentage/100);
    
    // # Calculate net revenue (after budget)
    const netRevenue = grossRevenue - budgetAllocation;
    
    additionalEntries[product] = {
      grossRevenue,
      budgetAllocation,
      netRevenue,
      memberShare: netAmount,
      sharePercentage
    };
  });
  
  // # Combine original and additional entries
  const combinedEntries = {
    ...(revenueData.originalAmounts || {}),
    ...additionalEntries
  };

  return (
    <div className="bnct_calc-container">
      <h3 className="bnct_calc-title">{isProjectBudget ? 'Budget Calculation Breakdown' : 'Revenue Calculation Breakdown'}</h3>
      
      {/* # Revenue Calculation Table - Only show for team members */}
      {!isProjectBudget && (
        <div className="bnct_calc-table-container">
          <table className="bnct_calc-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Gross Revenue (EGP)</th>
                <th>Budget (5%)</th>
                <th>Net Revenue (95%)</th>
                <th>{member.name}'s Share</th>
                <th>Share %</th>
              </tr>
            </thead>
            <tbody>
              {/* # Library Products */}
              {Object.entries(combinedEntries).filter(([key]) => 
                key.includes('library') || 
                key.includes('step1_library') || 
                key.includes('step2_library')
              ).map(([product, data]) => (
                <tr key={product} className="bnct_calc-category">
                  <td>{formatProductName(product)}</td>
                  <td>E£ {(data.grossRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>E£ {(data.budgetAllocation || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (5%)</td>
                  <td>E£ {(data.netRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (95%)</td>
                  <td>E£ {(data.memberShare || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>{((data.sharePercentage || 0) > 1 ? (data.sharePercentage || 0) : (data.sharePercentage || 0) * 100).toFixed(2)}%</td>
                </tr>
              ))}
              
              {/* # Premium Products */}
              {Object.entries(combinedEntries).filter(([key]) => 
                key.includes('premium') && 
                !key.includes('as_mentor') && 
                !key.includes('as_team_member')
              ).map(([product, data]) => (
                <tr key={product} className="bnct_calc-category">
                  <td>{formatProductName(product)}</td>
                  <td>E£ {(data.grossRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>E£ {(data.budgetAllocation || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (5%)</td>
                  <td>E£ {(data.netRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (95%)</td>
                  <td>E£ {(data.memberShare || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>{((data.sharePercentage || 0) > 1 ? (data.sharePercentage || 0) : (data.sharePercentage || 0) * 100).toFixed(2)}%</td>
                </tr>
              ))}
              
              {/* # Mentorship Products - As Mentor - Egyptian */}
              {Object.entries(combinedEntries).filter(([key]) => 
                key.includes('mentorship') && 
                key.includes('as_mentor') && 
                key.includes('egyptian')
              ).map(([product, data]) => (
                <tr key={product} className="bnct_calc-category">
                  <td>Step {product.includes('step1') ? '1' : '2'} Mentorship {product.includes('monthly') ? 'Monthly' : 'Premium'} (As Mentor - Egyptian Users)</td>
                  <td>E£ {(data.grossRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>E£ {(data.budgetAllocation || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (5%)</td>
                  <td>E£ {(data.netRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (95%)</td>
                  <td>E£ {(data.memberShare || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>{((data.sharePercentage || 0) > 1 ? (data.sharePercentage || 0) : (data.sharePercentage || 0) * 100).toFixed(2)}%</td>
                </tr>
              ))}
              
              {/* # Mentorship Products - As Mentor - International */}
              {Object.entries(combinedEntries).filter(([key]) => 
                key.includes('mentorship') && 
                key.includes('as_mentor') && 
                key.includes('international')
              ).map(([product, data]) => (
                <tr key={product} className="bnct_calc-category">
                  <td>Step {product.includes('step1') ? '1' : '2'} Mentorship {product.includes('monthly') ? 'Monthly' : 'Premium'} (As Mentor - International Users)</td>
                  <td>E£ {(data.grossRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>E£ {(data.budgetAllocation || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (5%)</td>
                  <td>E£ {(data.netRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (95%)</td>
                  <td>E£ {(data.memberShare || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>{((data.sharePercentage || 0) > 1 ? (data.sharePercentage || 0) : (data.sharePercentage || 0) * 100).toFixed(2)}%</td>
                </tr>
              ))}
              
              {/* # Mentorship Products - As Team Member - Egyptian */}
              {Object.entries(combinedEntries).filter(([key]) => 
                key.includes('mentorship') && 
                key.includes('as_team_member') && 
                key.includes('egyptian')
              ).map(([product, data]) => (
                <tr key={product} className="bnct_calc-category">
                  <td>Step {product.includes('step1') ? '1' : '2'} Mentorship {product.includes('monthly') ? 'Monthly' : 'Premium'} (As Team Member - Egyptian Users)</td>
                  <td>E£ {(data.grossRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>E£ {(data.budgetAllocation || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (5%)</td>
                  <td>E£ {(data.netRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (95%)</td>
                  <td>E£ {(data.memberShare || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>{((data.sharePercentage || 0) > 1 ? (data.sharePercentage || 0) : (data.sharePercentage || 0) * 100).toFixed(2)}%</td>
                </tr>
              ))}
              
              {/* # Mentorship Products - As Team Member - International */}
              {Object.entries(combinedEntries).filter(([key]) => 
                key.includes('mentorship') && 
                key.includes('as_team_member') && 
                key.includes('international')
              ).map(([product, data]) => (
                <tr key={product} className="bnct_calc-category">
                  <td>Step {product.includes('step1') ? '1' : '2'} Mentorship {product.includes('monthly') ? 'Monthly' : 'Premium'} (As Team Member - International Users)</td>
                  <td>E£ {(data.grossRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>E£ {(data.budgetAllocation || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (5%)</td>
                  <td>E£ {(data.netRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (95%)</td>
                  <td>E£ {(data.memberShare || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>{((data.sharePercentage || 0) > 1 ? (data.sharePercentage || 0) : (data.sharePercentage || 0) * 100).toFixed(2)}%</td>
                </tr>
              ))}
              
              {/* # Other Products (not categorized above) */}
              {Object.entries(combinedEntries).filter(([key]) => 
                !key.includes('library') && 
                !key.includes('premium') && 
                !key.includes('mentorship')
              ).map(([product, data]) => (
                <tr key={product} className="bnct_calc-category">
                  <td>{formatProductName(product)}</td>
                  <td>E£ {(data.grossRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>E£ {(data.budgetAllocation || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (5%)</td>
                  <td>E£ {(data.netRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (95%)</td>
                  <td>E£ {(data.memberShare || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>{((data.sharePercentage || 0) > 1 ? (data.sharePercentage || 0) : (data.sharePercentage || 0) * 100).toFixed(2)}%</td>
                </tr>
              ))}
              
              {/* # Total Row */}
              <tr className="bnct_calc-total">
                <td>Total</td>
                <td>E£ {Object.values(combinedEntries).reduce((sum, data) => sum + (data.grossRevenue || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td>E£ {Object.values(combinedEntries).reduce((sum, data) => sum + (data.budgetAllocation || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td>E£ {Object.values(combinedEntries).reduce((sum, data) => sum + (data.netRevenue || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td>E£ {(revenueData.netRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td>Varies</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      
      {/* # Budget Calculation Table - Only show for project budget */}
      {isProjectBudget && (
        <div className="bnct_calc-table-container">
          <table className="bnct_calc-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Budget Allocation</th>
                <th>% of Total Budget</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(revenueData.budgetByProduct || {}).map(([product, amount]) => (
                <tr key={product} className="bnct_calc-category">
                  <td>{formatProductName(product)}</td>
                  <td>E£ {(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>{(((amount || 0) / (revenueData.budgetAllocation || 1)) * 100).toFixed(2)}%</td>
                </tr>
              ))}
              
              {/* # Total Row */}
              <tr className="bnct_calc-total">
                <td>Total</td>
                <td>E£ {(revenueData.budgetAllocation || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td>100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      
      {/* # Revenue Share Formulas - Only show for team members */}
      {!isProjectBudget && (
        <>
          <h3 className="bnct_calc-title">Revenue Share Formulas</h3>
          <div className="bnct_calc-formulas">
            <div className="bnct_calc-formula-card">
              <h4>Library Subscriptions</h4>
              <ul>
                <li>Elhadidy: 28% of net revenue</li>
                <li>Hussein: 20% of net revenue</li>
                <li>Ayman: 20% of net revenue</li>
                <li>Hossam: 20% of net revenue</li>
                <li>Nabil: 12% of net revenue</li>
              </ul>
            </div>
            
            <div className="bnct_calc-formula-card">
              <h4>Premium Subscriptions</h4>
              <ul>
                <li>Elhadidy: 22.125% of net revenue</li>
                <li>Hussein: 22.125% of net revenue</li>
                <li>Ayman: 22.125% of net revenue</li>
                <li>Hossam: 22.125% of net revenue</li>
                <li>Nabil: 11.5% of net revenue</li>
              </ul>
            </div>
            
            <div className="bnct_calc-formula-card">
              <h4>Mentorship (Egyptian Users)</h4>
              <ul>
                <li>Assigned Mentor: 50% of net revenue</li>
                <li>Other Team Members: 12.5% each of net revenue</li>
              </ul>
            </div>
            
            <div className="bnct_calc-formula-card">
              <h4>Mentorship (International Users)</h4>
              <ul>
                <li>Assigned Mentor: 33% of net revenue</li>
                <li>Other Team Members: 16.75% each of net revenue</li>
              </ul>
            </div>
          </div>
        </>
      )}
      
      {/* # Budget Allocation Formula - Only show for project budget */}
      {isProjectBudget && (
        <>
          <h3 className="bnct_calc-title">Budget Allocation Formula</h3>
          <div className="bnct_calc-formulas">
            <div className="bnct_calc-formula-card">
              <h4>Budget Source</h4>
              <p>The project budget receives 5% of the gross revenue from all product sales.</p>
            </div>
            
            <div className="bnct_calc-formula-card">
              <h4>Budget Calculation</h4>
              <p>Budget Allocation = Gross Revenue × 5%</p>
            </div>
          </div>
        </>
      )}
      
      {/* # Revenue Calculation Methodology */}
      <div className="bnct_calc-methodology">
        <h3 className="bnct_calc-title">{isProjectBudget ? 'Budget Calculation Methodology' : 'Revenue Calculation Methodology'}</h3>
        <div className="bnct_calc-methodology-content">
          {isProjectBudget ? (
            // # Budget methodology steps
            <>
              <div className="bnct_calc-methodology-step">
                <h4>Step 1: Collect Budget Allocation</h4>
                <p>5% of gross revenue from all product sales is allocated to the project budget.</p>
              </div>
              
              <div className="bnct_calc-methodology-step">
                <h4>Step 2: Track Expenses</h4>
                <p>All project expenses are recorded and categorized.</p>
              </div>
              
              <div className="bnct_calc-methodology-step">
                <h4>Step 3: Calculate Net Budget</h4>
                <p>Net Budget = Total Budget Allocation - Total Expenses</p>
              </div>
              
              <div className="bnct_calc-methodology-step">
                <h4>Step 4: Determine Available Budget</h4>
                <p>The remaining budget after all expenses is available for future use.</p>
              </div>
            </>
          ) : (
            // # Revenue methodology steps
            <>
              <div className="bnct_calc-methodology-step">
                <h4>Step 1: Calculate Gross Revenue</h4>
                <p>Total amount received from customers before any deductions.</p>
              </div>
              
              <div className="bnct_calc-methodology-step">
                <h4>Step 2: Allocate Budget (5%)</h4>
                <p>5% of gross revenue is allocated to the project budget account.</p>
              </div>
              
              <div className="bnct_calc-methodology-step">
                <h4>Step 3: Calculate Net Revenue (95%)</h4>
                <p>95% of gross revenue is distributed among team members.</p>
              </div>
              
              <div className="bnct_calc-methodology-step">
                <h4>Step 4: Apply Share Percentages</h4>
                <p>Each team member receives their share based on the product type and their role.</p>
              </div>
              
              <div className="bnct_calc-methodology-step">
                <h4>Step 5: Deduct Salaries</h4>
                <p>Monthly salaries are deducted from each member's revenue share.</p>
              </div>
              
              <div className="bnct_calc-methodology-step">
                <h4>Step 6: Calculate Available Cash</h4>
                <p>Net revenue minus salary deductions equals available cash.</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// # Helper function to get share percentage based on member ID and product type
const getSharePercentage = (memberId, productType) => {
  // # Library subscriptions
  if (productType.includes('library')) {
    if (memberId === 'nabil_mostafa') return 12;
    return 20; // # For other team members (hadidy: 28)
  }
  
  // # Premium subscriptions
  if (productType.includes('premium')) {
    if (memberId === 'nabil_mostafa') return 11.5;
    return 22.125; // # For other team members
  }
  
  // # For mentorship, we can't calculate exact percentage without knowing
  // # if they were the mentor and the nationality, so return an average
  return 20; // # Approximate average share
};

// # Helper function to format product names for better display
const formatProductName = (product) => {
  if (!product) return 'Unknown Product';
  
  // # Replace underscores with spaces and capitalize words
  let formattedName = product.replace(/_/g, ' ');
  
  // # Handle specific product types
  if (formattedName.includes('step1 library')) {
    return 'Step 1 Library Subscription';
  } else if (formattedName.includes('step2 library')) {
    return 'Step 2 Library Subscription';
  } else if (formattedName.includes('step1 premium')) {
    return 'Step 1 Premium Subscription';
  } else if (formattedName.includes('step2 premium')) {
    return 'Step 2 Premium Subscription';
  } else if (formattedName.includes('step1 mentorship')) {
    return 'Step 1 Mentorship';
  } else if (formattedName.includes('step2 mentorship')) {
    return 'Step 2 Mentorship';
  } else if (formattedName.includes('OET')) {
    return 'OET Mentorship';
  } else if (formattedName.includes('US VISA')) {
    return 'US VISA Counselling';
  }
  
  // # Capitalize first letter of each word
  return formattedName.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default RevenueCalculations; 